// src/utils/promoRedemption.js
//
// Coordination between the signup flow and the global promo-redeem modal host.
//
// Flow:
//  1. A user arrives via a promo link (…?promo=CODE). `capturePromoCodeFromSearch`
//     stashes the normalized code in sessionStorage (survives the SPA navigation
//     through sign-up).
//  2. When a brand-new account is created (any auth method), `armPromoForSignup`
//     sets a one-shot "just signed up" flag.
//  3. On the landing screen the modal host calls `consumeSignupArm`; if it was
//     armed AND a pending code exists, it opens the redeem modal pre-filled.
//
// The armed flag guarantees the modal only appears immediately after a successful
// signup — an already-signed-in user opening a promo link will not auto-open it.

const PENDING_PROMO_CODE_KEY = "promo.pendingCode";
const PROMO_SIGNUP_ARMED_KEY = "promo.signupArmed";

const MIN_CODE_LEN = 4;
const MAX_CODE_LEN = 24;

function safeSession() {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/** Normalize to the same shape the callable expects: UPPERCASE alphanumerics. */
export function normalizePromoCode(raw) {
  return String(raw || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

function isPlausibleCode(code) {
  return code.length >= MIN_CODE_LEN && code.length <= MAX_CODE_LEN;
}

export function readPendingPromoCode() {
  const store = safeSession();
  if (!store) return "";
  const code = normalizePromoCode(store.getItem(PENDING_PROMO_CODE_KEY));
  return isPlausibleCode(code) ? code : "";
}

export function setPendingPromoCode(code) {
  const store = safeSession();
  if (!store) return "";
  const normalized = normalizePromoCode(code);
  if (!isPlausibleCode(normalized)) return "";
  try {
    store.setItem(PENDING_PROMO_CODE_KEY, normalized);
  } catch {
    /* storage full / unavailable — non-fatal */
  }
  return normalized;
}

/**
 * Read `?promo=CODE` from a location.search string and, if present & plausible,
 * persist it. Returns the captured code (or "" if none). The caller strips the
 * param from the URL so it doesn't linger or re-trigger.
 */
export function capturePromoCodeFromSearch(search) {
  let params;
  try {
    params = new URLSearchParams(search || "");
  } catch {
    return "";
  }
  const raw = params.get("promo");
  if (!raw) return "";
  return setPendingPromoCode(raw);
}

/** Mark that a brand-new account was just created (one-shot). */
export function armPromoForSignup() {
  const store = safeSession();
  if (!store) return;
  try {
    store.setItem(PROMO_SIGNUP_ARMED_KEY, "1");
  } catch {
    /* non-fatal */
  }
}

/**
 * Consume the one-shot "just signed up" flag. Returns true exactly once after a
 * fresh signup; clears the flag so later navigations don't re-open the modal.
 */
export function consumeSignupArm() {
  const store = safeSession();
  if (!store) return false;
  const armed = store.getItem(PROMO_SIGNUP_ARMED_KEY) === "1";
  if (armed) {
    try {
      store.removeItem(PROMO_SIGNUP_ARMED_KEY);
    } catch {
      /* non-fatal */
    }
  }
  return armed;
}

/** Clear all promo-redemption state (after redeem, dismiss, or a full reset). */
export function clearPromoRedemption() {
  const store = safeSession();
  if (!store) return;
  try {
    store.removeItem(PENDING_PROMO_CODE_KEY);
    store.removeItem(PROMO_SIGNUP_ARMED_KEY);
  } catch {
    /* non-fatal */
  }
}
